import { forwardRef } from 'react';
import PropTypes from 'prop-types';

import { zones } from 'services/zones';
import { getPageConfig } from 'services/grid-service';
import { rackList } from 'services/rackList';
import { ResetBtn, UseBtn } from 'libs/ui';
import {
  DictFilter,
  Input,
  Container,
  ButtonsContainer,
  DoubleField,
  DateSelect,
  DateTime,
  Multiselect,
  ZoneInput,
  Toggle,
  ParkingFilter,
  ClassFilter,
  CategoryFilter,
  AppFilter,
  SourceFilter,
  RoleFilter,
  SelectFilter,
} from '../index';

// {type: 'input', w: 160, name: 'id', title: 'ID', placeholder: '3786467864675656'},
// {type: 'dict', w: 140, dict: 'clients.status',},

export const Filters = forwardRef((props, ref) => {
  const { items, page, handleSubmit, reset, theme, t } = props;
  const pageConfig = getPageConfig(page);
  const activeFilters = {};
  pageConfig?.filters?.forEach((el) => {
    activeFilters[el.name] = el.active;
  });

  /**
   * get element component
   * @param {*} type
   * @returns
   */
  const getComponent = (type) => {
    switch (type) {
      case 'dict':
        return DictFilter;

      default:
        return Input;
    }
  };

  /**
   * get attributes for type
   * @param {*} other
   * @param {*} type
   * @returns
   */
  const getAttr = (other, type) => {
    const out = {};
    const t = {
      input: {
        title: 'label',
        w: 'width',
      },
      zones: {
        w: 'width',
      },
      dict: {},
    };

    Object.keys(other).forEach((fn) => {
      let val = other[fn];
      const name = (t[type] && t[type][fn]) ?? fn;
      if (type !== 'dict' && fn === 'w') {
        val += 'px';
      }
      out[name] = val;
    });

    if (type === 'input' && !out['type']) {
      out['type'] = 'text';
    }

    return out;
  };

  /**
   * get double date input
   * @param {*} key
   * @param {*} attr
   * @returns
   */
  const getDoubleDate = (key, attr) => {
    const { name, title, ...rest } = attr;
    const fn1 = name + '_from';
    const fn2 = name + '_to';
    return (
      <DateSelect key={key} from_name={fn1} to_name={fn2} {...rest}>
        <DoubleField>
          <DateTime width="160px" name={fn1} type="dateTime" label={title} />
          <DateTime width="160px" name={fn2} type="dateTime" />
        </DoubleField>
      </DateSelect>
    );
  };

  /**
   * get mean double
   * @param {*} key
   * @param {*} attr
   * @returns
   */
  const getMeanDouble = (key, attr) => {
    const { name, title, dict, w, placeholder, multiple, ...rest } = attr;
    return (
      <DoubleField key={key}>
        <DictFilter dict={dict} multiple={multiple} />
        <Input width={w} name={name} type="text" label={title} placeholder={placeholder} {...rest} />
      </DoubleField>
    );
  };

  /**
   * get toggle
   * @param {*} key
   * @param {*} attr
   * @returns
   */
  const getToggle = (key, attr) => {
    const { name, title, dict, w, placeholder, ...rest } = attr;
    return (
      <span className="center" key={key} {...rest}>
        <Toggle label={title} name={name} type="toggle" />
      </span>
    );
  };

  /**
   * get duration
   * @param {*} key
   * @param {*} attr
   * @returns
   */
  const getDuration = (key, attr) => {
    const { name, title, ...rest } = attr;
    const fn1 = name + '_from';
    const fn2 = name + '_to';

    const mask = `99${t('filter.д')} 99${t('filter.ч')} 99${t('filter.м')}`;
    return (
      <DoubleField key={key} {...rest}>
        <Input width="110px" name={fn1} type="text" label={title} mask={mask} storeWithMask placeholder={t('filter.От')} />
        <Input width="110px" name={fn2} type="text" mask={mask} storeWithMask placeholder={t('filter.До')} />
      </DoubleField>
    );
  };

  /**
   * get double number
   * @param {*} key
   * @param {*} attr
   * @returns
   */
  const getDoubleNumber = (key, attr) => {
    const { name, title, withMinus, ...rest } = attr;
    const fn1 = name + '_from';
    const fn2 = name + '_to';
    return (
      <DoubleField key={key} {...rest}>
        <Input width="80px" name={fn1} type="text" label={title} naturalNumber withZero withMinus={withMinus} EmptyOnExit placeholder={t('filter.От')} />
        <Input width="80px" name={fn2} type="text" naturalNumber withZero withMinus={withMinus} EmptyOnExit placeholder={t('filter.До')} />
      </DoubleField>
    );
  };

  /**
   * get zones
   * @param {*} key
   * @param {*} attr
   * @returns
   */
  const getZones = (key, attr) => {
    const { title, ...rest } = attr;
    const zonesVals = zones.getZones();
    const zonesValsOk = zonesVals.map((el) => {
      const { background_color, color, id, name, parking_id, zone_id } = el;
      return {
        background_color,
        color,
        id,
        name,
        parking_id,
        zone_id,
      };
    });

    return <Multiselect key={key} type="multiselect" label={title} additionalComponent={ZoneInput} data={zonesValsOk} countDisplayedIcons={3} {...rest} />;
  };

  /**
   * get racks
   * @param {*} key
   * @param {*} attr
   * @returns
   */
  const getRacks = (key, attr) => {
    const { title, ...rest } = attr;
    const racks = rackList.getItems();
    return <Multiselect key={key} type="rackSelect" label={title} data={racks} {...rest} />;
  };

  /**
   * get classes
   * @param {*} key
   * @param {*} attr
   * @returns
   */
  const getClassesFlt = (key, attr) => {
    return <ClassFilter key={key} multiple {...attr} />;
  };

  /**
   * get categories
   * @param {*} key
   * @param {*} attr
   * @returns
   */
  const getCategoriesFlt = (key, attr) => {
    return <CategoryFilter key={key} {...attr} />;
  };

  /**
   * get filter items
   * @returns
   */
  const getItems = () => {
    const isActive = (el) => {
      if (el.isHidden) {
        return false;
      }
      let test = el.dict || el.name;
      if (!test) {
        return true;
      }
      if (!activeFilters) {
        return true;
      }
      test = test.split('.');
      test = test.length > 1 ? test[1] : test[0];
      if (activeFilters[test] === undefined) {
        return true;
      }
      return activeFilters[test];
    };
    const filtered = items.filter((el) => isActive(el));
    // console.log('🚀 ~ getItems ~ filtered:', filtered, active);
    const cnt = filtered.length;

    return filtered.map((el, idx) => {
      const { type, ...other } = el;
      const attr = getAttr(other, type);
      delete attr.isHidden;

      switch (type) {
        case 'select':
          return <SelectFilter key={idx} {...attr} />;
        case 'role':
          return <RoleFilter key={idx} {...attr} />;
        case 'source':
          return <SourceFilter key={idx} {...attr} />;
        case 'app':
          return <AppFilter key={idx} {...attr} />;
        case 'categories':
          return getCategoriesFlt(idx, attr);
        case 'classes':
          return getClassesFlt(idx, attr);
        case 'racks':
          return getRacks(idx, attr);
        case 'zones':
          return getZones(idx, attr);
        case 'doubledate':
          return getDoubleDate(idx, attr);
        case 'duration':
          return getDuration(idx, attr);
        case 'meandouble':
          return getMeanDouble(idx, attr);
        case 'doublenumber':
          return getDoubleNumber(idx, attr);
        case 'toggle':
          return getToggle(idx, attr);
        case 'parking':
          return <ParkingFilter key={idx} {...attr} />;
        default:
          if (cnt === idx + 1) {
            attr.lastField = true;
          }

          const Cmp = getComponent(type);
          return <Cmp key={idx} {...attr} />;
      }
    });
  };

  const itemsEl = getItems();
  if (!itemsEl.length) {
    return null;
  }

  const cl = 'cp-filter';

  return (
    <Container theme={theme} className={cl}>
      <form onSubmit={handleSubmit} ref={ref}>
        {itemsEl}
        <ButtonsContainer>
          <ResetBtn onClick={reset} />
          <UseBtn />
        </ButtonsContainer>
      </form>
    </Container>
  );
});

Filters.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  items: PropTypes.array.isRequired,
  page: PropTypes.string.isRequired,
};
